import { HubConnectionBuilder, HubConnectionState, type HubConnection, LogLevel } from "@microsoft/signalr";
import { getConfig } from "../config";
import { EventTracker } from "./eventDedupe";

// SignalR call-control client (docs/design/modules/05-realtime-protocols.md).
// Wraps the connection with the per-call seq de-dupe and the reconnect/resume algorithm
// (§4): on transport reconnect it calls Reconnect(callId, lastSeq) to replay missed
// events; a resync-required ack triggers a full state refresh via onResync.

export interface CallEvent {
  event: string;
  seq: number;
  correlationId: string;
  data: Record<string, unknown>;
}

export interface HubAck {
  ok: boolean;
  error?: { code: string; message: string };
}

export interface RegisterResult {
  userId: number;
  displayName: string;
  roles: string[];
}

export type CallEventHandler = (ev: CallEvent) => void;

/**
 * Returns the connection only when it is in the Connected state, otherwise null.
 * Guards CallHubClient.invoke against the SignalR internal synchronous throw
 * ("Cannot send data if the connection is not in the 'Connected' State") that
 * escapes async try/catch in some bundler environments. Exported for unit testing.
 */
export function resolveConnection(conn: HubConnection | undefined): HubConnection | null {
  if (!conn || conn.state !== HubConnectionState.Connected) return null;
  return conn;
}

/**
 * Strip any `access_token` query param from the configured hub URL. The token is supplied
 * through SignalR's accessTokenFactory, which appends exactly one `access_token` for the
 * WebSocket transport; a second one from the URL would be comma-joined server-side and
 * rejected as an invalid token. Pure + unit-tested.
 */
export function sanitizeHubUrl(url: string): string {
  const qIndex = url.indexOf("?");
  if (qIndex < 0) return url;
  const base = url.slice(0, qIndex);
  const kept = url
    .slice(qIndex + 1)
    .split("&")
    .filter((part) => part.length > 0 && !/^access_token=/i.test(part));
  return kept.length > 0 ? `${base}?${kept.join("&")}` : base;
}

export class CallHubClient {
  private connection?: HubConnection;
  private readonly tracker = new EventTracker();
  private currentCallId: number | null = null;

  constructor(
    private readonly onEvent: CallEventHandler,
    private readonly onResync: (callId: number) => void
  ) {}

  async connect(token: string): Promise<void> {
    const cfg = getConfig();
    // Supply the token ONLY via accessTokenFactory — SignalR appends exactly one
    // access_token query param for the WebSocket transport (the hub reads
    // Request.Query["access_token"]). Passing it in the URL as well duplicates the
    // param, which ASP.NET joins with a comma → "invalid token". sanitizeHubUrl strips
    // any access_token the configured URL may carry, defensively.
    const conn = new HubConnectionBuilder()
      .withUrl(sanitizeHubUrl(cfg.hubUrl), { accessTokenFactory: () => token })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on("event", (env: CallEvent) => this.dispatch(env));
    conn.onreconnected(() => void this.resume());

    this.connection = conn;
    await conn.start();
  }

  /** Track the active call so reconnect knows what to resume and how to attribute events. */
  setCurrentCall(callId: number | null): void {
    this.currentCallId = callId;
  }

  private dispatch(env: CallEvent): void {
    const callId = Number((env.data?.["callId"] as number | undefined) ?? this.currentCallId ?? 0);
    if (!this.tracker.shouldApply(callId, env.seq)) return; // drop duplicate / replay overlap
    this.onEvent(env);
  }

  private async resume(): Promise<void> {
    if (this.currentCallId == null || !this.connection) return;
    const ack = await this.connection.invoke<HubAck>("Reconnect", this.currentCallId, this.tracker.lastSeqFor(this.currentCallId));
    if (!ack.ok && ack.error?.code === "VC-RESYNC") this.onResync(this.currentCallId);
  }

  register(): Promise<RegisterResult> {
    return this.invoke<RegisterResult>("Register");
  }

  setPresence(status: "available" | "unavailable"): Promise<HubAck> {
    return this.invoke<HubAck>("SetPresence", status);
  }

  invite(callId: number, officerUserId: number): Promise<HubAck> {
    return this.invoke<HubAck>("Invite", callId, officerUserId);
  }

  acceptInvite(callId: number): Promise<HubAck> {
    return this.invoke<HubAck>("AcceptInvite", callId);
  }

  declineInvite(callId: number): Promise<HubAck> {
    return this.invoke<HubAck>("DeclineInvite", callId);
  }

  endCall(callId: number): Promise<HubAck> {
    return this.invoke<HubAck>("EndCall", callId);
  }

  /** Join the call group of a server-started call this user is a party to (public flow). */
  joinCall(callId: number): Promise<HubAck> {
    return this.invoke<HubAck>("JoinCall", callId);
  }

  async stop(): Promise<void> {
    await this.connection?.stop();
    this.connection = undefined;
  }

  private invoke<T>(method: string, ...args: unknown[]): Promise<T> {
    const conn = resolveConnection(this.connection);
    if (!conn) return Promise.reject(new Error("hub not connected"));
    return conn.invoke<T>(method, ...args);
  }
}
